import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

type DuplicateField = "phone" | "email" | "cnpj";
type DuplicateResult = Partial<Record<DuplicateField, boolean>>;

export const useSupplierDuplicateCheck = () => {
  const [duplicates, setDuplicates] = useState<DuplicateResult>({});
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const checkField = useCallback((field: DuplicateField, value: string) => {
    // Clear previous timer for this field
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    // Min length guards
    const digits = value.replace(/\D/g, "");
    if (field === "phone" && digits.length !== 11) return;
    if (field === "cnpj" && digits.length !== 14) return;
    if (field === "email" && (!value.includes("@") || value.length < 5)) return;

    debounceTimers.current[field] = setTimeout(async () => {
      try {
        const { data } = await supabase.functions.invoke("check-supplier-duplicate", {
          body: { [field]: value },
        });
        if (data && typeof data[field] === "boolean") {
          setDuplicates((prev) => ({ ...prev, [field]: data[field] }));
        }
      } catch {
        // silently fail
      }
    }, 600);
  }, []);

  const resetField = useCallback((field: DuplicateField) => {
    setDuplicates((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  return { duplicates, checkField, resetField };
};
